import { useEffect, useRef, useState } from 'react'
import { Zap, X, Send } from 'lucide-react'
import api from '../api/client'
import { useAuth } from '../auth'

// IA TheConect 360° — globo orbital flutuante + chat (Ctrl+X abre/fecha).
// Porte fiel do widget original (k6). Não alterar a lógica da IA.
export default function IAWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [msgs, setMsgs] = useState([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    function onKey(e) { if (e.ctrlKey && e.key === 'x') { e.preventDefault(); setOpen((v) => !v) } }
    function onOpen() { setOpen(true) }   // disparado pelo menu Integrações › IA
    window.addEventListener('keydown', onKey)
    window.addEventListener('tc-ia-open', onOpen)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('tc-ia-open', onOpen) }
  }, [])

  useEffect(() => {
    if (open && msgs.length === 0) {
      const nome = (user?.nome || '').split(' ')[0]
      setMsgs([{ tipo: 'ia', texto: `Olá ${nome}! Sou a IA TheConect 360°, assistente inteligente da The Conect Energy. Tenho acesso ao sistema — pergunte o que precisar.` }])
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100)
  }, [open]) // eslint-disable-line
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight }, [msgs, busy])

  if (!user) return null

  async function send() {
    if (!text.trim() || busy) return
    const m = text.trim()
    setText(''); setMsgs((p) => [...p, { tipo: 'user', texto: m }]); setBusy(true)
    try {
      const { data } = await api.post('/chat/message', { message: m, history: msgs.slice(-20) })
      setMsgs((p) => [...p, { tipo: 'ia', texto: data.response || 'Sem resposta.' }])
    } catch {
      setMsgs((p) => [...p, { tipo: 'ia', texto: 'Erro ao conectar com a IA. Tente novamente.' }])
    } finally { setBusy(false) }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-50 group" aria-label="Abrir IA TheConect 360°">
        <div className="relative w-20 h-20 flex items-center justify-center">
          <div className="absolute inset-[-6px] rounded-full bg-primary-500/20 blur-md tc-globe-glow" />
          <div className="absolute inset-[-12px] rounded-full bg-primary-500/10 blur-xl tc-globe-glow" style={{ animationDelay: '1s' }} />
          <div className="absolute inset-[-8px] tc-orbit-ring" style={{ animationDuration: '4s' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-primary-500 rounded-full shadow-[0_0_8px_2px_rgba(255,108,0,0.6)]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1.5 h-1.5 bg-primary-500/50 rounded-full shadow-[0_0_6px_2px_rgba(255,108,0,0.3)]" />
          </div>
          <div className="absolute inset-[-4px] tc-orbit-ring" style={{ animationDuration: '6s', animationDirection: 'reverse', transform: 'rotateZ(60deg)' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-primary-500/80 rounded-full shadow-[0_0_8px_2px_rgba(255,108,0,0.5)]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1.5 h-1.5 bg-primary-500/40 rounded-full" />
          </div>
          <div className="absolute inset-[-8px] rounded-full border border-primary-500/20 tc-orbit-ring" style={{ animationDuration: '10s' }} />
          <div className="absolute inset-[-4px] rounded-full border border-dashed border-primary-500/15 tc-orbit-ring" style={{ animationDuration: '14s', animationDirection: 'reverse' }} />
          <div className="relative w-14 h-14 rounded-full overflow-hidden group-hover:scale-110 transition-transform shadow-[0_0_20px_4px_rgba(255,108,0,0.4)] tc-globe-glow border-2 border-primary-500/50 bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center">
            <Zap size={28} className="text-white drop-shadow-lg" />
          </div>
        </div>
        <div className="mt-1 text-center">
          <p className="text-[9px] font-black tracking-wider leading-none text-primary-600">IA TheConect</p>
          <p className="text-[11px] font-black tracking-wide text-gray-700">360°</p>
        </div>
      </button>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[550px] sm:w-[820px] sm:h-[88vh] sm:max-h-[920px] max-h-[calc(100vh-2rem)] bg-white rounded-3xl shadow-2xl shadow-black/20 border border-gray-100 overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-graphite via-neutral-900 to-accent-700 px-5 py-4 flex items-center gap-3 shrink-0">
        <div className="relative w-10 h-10">
          <div className="absolute inset-[-2px] rounded-full border border-primary-400/40 animate-spin" style={{ animationDuration: '6s' }} />
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center border-2 border-primary-400/30">
            <Zap size={20} className="text-white" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-sm">IA TheConect 360°</h3>
          <p className="text-accent-300 text-xs">Assistente Inteligente</p>
        </div>
        <button onClick={() => setOpen(false)} title="Fechar"
          className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors flex-shrink-0">
          <X size={20} />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto tc-scroll p-4 space-y-3 bg-gray-50">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.tipo === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.tipo === 'user' ? 'bg-gradient-to-br from-primary-500 to-accent-500 text-white rounded-br-md' : 'bg-white border border-gray-100 text-gray-700 rounded-bl-md shadow-sm'}`}>{m.texto}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm"><span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-accent-400 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-accent-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 bg-accent-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} /></span></div></div>}
      </div>

      <div className="p-3 border-t border-gray-100 bg-white shrink-0 flex items-center gap-2">
        <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Pergunte à IA..." className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-accent-400" />
        <button onClick={send} disabled={busy || !text.trim()} className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 text-white flex items-center justify-center disabled:opacity-40 hover:brightness-95 transition-all">
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}
